import React, { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useBreakpoint, useGA } from '~/hooks'
import { TradeDirection } from '../../types'
import Token from '~/components/Token'
import TokenListModal from '../TokenListModal'
import {
  StyledMainInputContainer,
  StyledInput,
  StyledInputWrapper,
  StyledInputValue,
} from './styled'
import { TokenInputRowProps } from './types'
import { useTokenPriceWithUseAPI } from '~/hooks/useTokenPrice'
import BigNumber from 'bignumber.js'
import { Currency, FixedPointNumber } from '~/utils/fixedPoint'
import { useSpotSwapPair, useSpotSwapAmount } from '~/state/dex/hooks'
import { formatFormAmount } from '~/utils/numbers'
import { showSkeletonStateList } from '../../helpers'
import PriceImpact from '../OrderPreview/PriceImpact'
import Skeleton from '../Skeleton'

const TokenInputRow: React.FC<TokenInputRowProps> = ({
  direction,
  handleInputFocus,
  handleInputChange,
  onSelectToken,
  rateState,
  style,
  hasTokenPrice = true,
  orderRefreshing = false,
}: TokenInputRowProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [inputToken, outputToken] = useSpotSwapPair()
  const { inputAmount, outputAmount } = useSpotSwapAmount()
  const isInputDirection = direction === TradeDirection.Input

  const onClick = () => {
    trackClick(`webtl_instant_${direction}`)
    setIsOpen(true)
  }

  const { isMobile } = useBreakpoint()
  const { t } = useTranslation()
  const { trackClick } = useGA()
  const loading = showSkeletonStateList.includes(rateState)
  const token = isInputDirection ? inputToken : outputToken
  const tokenUnitPrice = useTokenPriceWithUseAPI({
    tokenAddress: token?.address,
    chainId: token?.chainId,
  })
  let placeholder = isInputDirection
    ? t('amount_for_sell')
    : t('amount_for_buy')
  let exchangeAmount = isInputDirection ? inputAmount : outputAmount
  let exchangeAmountLiteral =
    !exchangeAmount || isNaN(Number(exchangeAmount))
      ? ''
      : !isInputDirection
      ? formatFormAmount(exchangeAmount)
      : exchangeAmount

  if (inputAmount && loading && !isInputDirection) {
    exchangeAmountLiteral = ''
    placeholder = '......'
  }

  const showInput = useMemo(() => {
    return (
      // 当输入框是inputToken时固定显示
      isInputDirection ||
      // 当没有输入inputAmount固定展示输入框，不显示动画
      !(inputAmount && loading) ||
      // 输入框是outputToken时，当 订单不在刷新状态 时才显示
      !orderRefreshing
    )
  }, [isInputDirection, inputAmount, loading, orderRefreshing])

  const handleSelectToken = (
    inputToken: SpotSwapToken,
    outputToken: SpotSwapToken,
  ) => {
    const mode = isInputDirection ? 'maker' : 'taker'
    trackClick(`webtl_instant_${mode}_select`, {
      symbol: isInputDirection ? inputToken.symbol : outputToken.symbol,
      address: isInputDirection
        ? `${inputToken.address}`
        : `${outputToken.address}`,
    })
    onSelectToken(inputToken, outputToken)
    setIsOpen(false)
  }

  const usdValue = useMemo(() => {
    return new BigNumber(exchangeAmount).times(tokenUnitPrice)
  }, [exchangeAmount, tokenUnitPrice])

  /**
   * 用户没有输入数量的时候不展示价值，输入数量 0 展示价值 0
   */
  const usdValueLiteral =
    Number(exchangeAmount) === 0
      ? '$0'
      : Number(usdValue)
      ? `~${new FixedPointNumber(usdValue).toCurrency(Currency.USD)}`
      : '~'

  return (
    <StyledMainInputContainer style={style}>
      <TokenListModal
        isOpen={isOpen}
        onDismiss={() => setIsOpen(false)}
        direction={direction}
        handleSelectToken={handleSelectToken}
      />
      <Token token={token} onClick={onClick} />
      <StyledInputWrapper>
        {showInput ? (
          <StyledInput
            className="main-op-row-input"
            type={isMobile ? 'number' : 'text'}
            placeholder={placeholder}
            value={exchangeAmountLiteral}
            onFocus={() => handleInputFocus(direction)}
            onChange={(e) => handleInputChange(direction, e.target.value)}
          />
        ) : (
          <Skeleton />
        )}

        {!!exchangeAmount &&
          (showInput ? (
            <StyledInputValue>
              {usdValueLiteral}
              {/* show price impact when it's output */}
              {direction === TradeDirection.Output && hasTokenPrice && (
                <PriceImpact />
              )}
            </StyledInputValue>
          ) : (
            <Skeleton style={{ width: 60 }} />
          ))}
      </StyledInputWrapper>
    </StyledMainInputContainer>
  )
}

export default TokenInputRow
